/* eslint-disable no-extra-boolean-cast */
import { gtag, sendAnalyticsEvents } from 'google-analytics';
import angular from 'angular';
import {
  CartUpdateOptions,
  CartOrderManager,
  CartOrderItem,
  CartOrder,
  CartOrderCondition,
  CartConditionalNavigation,
  CartOrderPayment,
  CartOrderUpdateHttpResponse,
  CartOrderShipping,
  CartOrderPreviewTotalsHttpResponse,
  CartOrderTotals,
} from 'cart-core';
import { getConditionMessageByTagName } from './orderConditionHelpers';

type LoadingCallbackOptions = object;

type LoadingCallback = (
  name: string,
  amount: number,
  options?: LoadingCallbackOptions
) => void;

/**
 * apiUrlPaths configuration
 */
const apiUrlPaths = {
  orderApi: '/api/cart/order/{orderId}',
  orderItemApi: '/api/cart/order/{orderId}/item',
  orderItemBySkuApi: '/api/cart/order/{orderId}/item/{skuNumber}',
  preProcessCurrentOrderApi: '/api/cart/order/{orderId}/pre-process',
  processCurrentOrderApi: '/api/cart/order/{orderId}/process',
  removeAllItemsApi: '/api/cart/order/{orderId}/item',
};

class CheckoutOrderManager implements CartOrderManager {
  /**
   * Cached bag data
   */
  private order: CartOrder;

  /**
   * Cached messages from order response
   */
  public messages: string[] = [];

  public constructor(
    private $window: Window,
    private rcmEventManager: IRcmApiLibEventManager,
    private rcmApiLibService: IRcmApiLibService,
    private rcmApiLibMessageService: IRcmApiLibMessageService,
    private rcmLoading: IRcmLoading,
    private conditionalNavigation: CartConditionalNavigation
  ) {
    this.refreshOrder();
  }

  /**
   * loading - Expose set or change loading status
   * @param name unique namespace for this loading event
   * @param amount Loading amount between 0 and 1
   * @param options optional
   */
  public loadingListener = (
    name: string,
    amount: number,
    options?: LoadingCallbackOptions
  ) => {
    this.rcmLoading.setLoading(name, amount, options);
  };

  private buildLoadingListener(
    loadingListener?: LoadingCallback
  ): LoadingCallback {
    if (typeof loadingListener === 'function') {
      return loadingListener;
    }

    return () => {}; // noop
  }

  public getApiPath(key: string) {
    if (!apiUrlPaths[key]) {
      console.error('API Path ' + key + ' not found');
      return null;
    }

    return apiUrlPaths[key];
  }

  public getEventManager() {
    return this.rcmEventManager;
  }

  /**
   * Parse new bag data from server then throw the bag-changed event
   */
  private setBagDataFromResponse(
    response: CartOrderUpdateHttpResponse,
    execRequirements?: boolean,
    preProcessed?: boolean
  ) {
    this.rcmApiLibMessageService.buildPrimaryMessage(response.messages);

    this.order = response.data;
    if (response.messages) {
      this.messages = response.messages;
    } else {
      this.messages = [];
    }

    // We reset this state so calculate is only done when requested
    // @deprecated - Server will always calculate if it can
    // order.doCalculate = false;

    if (preProcessed) {
      this.order.preProcessed = true;
    }

    this.rcmEventManager.trigger('orderMgr.orderChange', this.order);

    if (typeof execRequirements === 'undefined') {
      execRequirements = true;
    }

    if (execRequirements) {
      this.executeCurrentPageRequirements();
    }
  }

  private onBeforeHttp() {
    this.messages = [];
    this.rcmApiLibMessageService.clearMessages();
  }

  private handleHttpError(response: CartOrderUpdateHttpResponse) {
    this.messages = response.messages;
    this.rcmApiLibMessageService.buildPrimaryMessage(response.messages);
  }

  /**
   * Determine what params to sent to the API
   */
  public getParams(params?: Record<string, any>) {
    if (typeof params === 'undefined') {
      params = {};
    }

    return params;
  }

  public hasOrder() {
    return Boolean(this.order?.id);
  }

  /**
   * Getter for bag data
   */
  public getOrder() {
    return this.order;
  }

  /**
   * Get the current order
   */
  public async getCurrentOrder(
    loadingListener?: LoadingCallback
  ): Promise<CartOrderUpdateHttpResponse> {
    if (this.hasOrder()) {
      return {
        data: this.order,
        messages: [],
      };
    }

    return await this.refreshOrder(loadingListener);
  }

  /**
   * Setter for order.
   *
   * Only really intended to be used during unit testing.
   */
  public setOrder(newOrder: CartOrder) {
    this.order = newOrder;
  }

  public getMessages() {
    return this.messages;
  }

  public getOrderAttribute(key: string, defaultValue: any) {
    if (typeof defaultValue === 'undefined') {
      defaultValue = null;
    }

    if (!this.order || !this.order.attributes) {
      return defaultValue;
    }

    if (key in this.order.attributes) {
      return this.order.attributes[key];
    }

    return defaultValue;
  }

  public getSkuQty(skuNum: string) {
    const item = this.getItem(skuNum);
    return item ? item.qty : 0;
  }

  /**
   * Does not transmit analytics.
   */
  private getAddItem(item: CartOrderItem) {
    item.qty = +this.getSkuQty(item.skuNumber) + +item.qty;
    return item;
  }

  public getItem(skuNumber: string) {
    return this.getOrderItem(this.order, skuNumber);
  }

  public getOrderItem(order: CartOrder, skuNumber: string) {
    return this.getItemFromList(order.items, skuNumber);
  }

  private getItemFromList(items: CartOrderItem[], skuNumber: string) {
    return items.find((item) => item.skuNumber == skuNumber);
  }

  public getItemsByType(type: string) {
    return this.order.items.filter((item) => item.type == type);
  }

  /**
   * buildItem and validate
   */
  private buildItem(item: CartOrderItem) {
    if (!item.skuNumber) {
      console.error('skuNumber is required for item');
      item.skuNumber = null;
    }
    if (typeof item.qty === 'undefined') {
      console.error('qty is required for item');
      item.qty = 0;
    }
    if (typeof item.type === 'undefined') {
      // console.error('type is required for item');
      item.type = null;
    }
    if (typeof item.data === 'undefined') {
      item.data = null;
    }
    return item;
  }

  /**
   * @deprecated Use setItem
   * Update item qty
   */
  public async setSkuQty(
    skuNum: string,
    qty: number,
    options: CartUpdateOptions
  ) {
    return await this.updateItem(
      {
        skuNumber: skuNum,
        qty: qty,
      },
      options
    );
  }

  public async addItem(item: CartOrderItem, options: CartUpdateOptions) {
    item = this.getAddItem(item);
    return await this.updateItem(item, options);
  }

  public async updateItem(item: CartOrderItem, options: CartUpdateOptions) {
    this.onBeforeHttp();

    item = this.buildItem(item);

    const previousItem = this.getOrderItem(this.order, item.skuNumber);
    this.rcmEventManager.trigger('orderMgr.updateItem.submit');

    return await this.withLoading('orderMgr.updateItem.submit', async () => {
      try {
        const response =
          await this.rcmApiLibService.put<CartOrderUpdateHttpResponse>({
            url: this.getApiPath('orderItemBySkuApi'),
            urlParams: { orderId: 'current', skuNumber: item.skuNumber },
            params: this.getParams(),
            data: item,
          });
        await this.sendCartUpdateAnalyticsEvents(item, options);
        this.setBagDataFromResponse(response);
        this.rcmEventManager.trigger('orderMgr.updateItem.success', {
          response: response,
          requestItem: item,
          previousItem,
        });
        return response;
      } catch (response) {
        this.handleHttpError(response);
        this.rcmEventManager.trigger('orderMgr.updateItem.error', {
          response,
          requestItem: item,
          previousItem,
        });
        throw response;
      }
    });
  }

  /**
   * @todo 2021-04-23 - bharms@relivinc.com - axosoft/25604:
   * No analytics tracking is currently performed by this function at all.
   * Add support for server-side tracking of when this function is called.
   */
  public async setItems(items: CartOrderItem[]) {
    this.onBeforeHttp();

    const previousItems: CartOrderItem[] = angular.copy(this.order.items);

    this.rcmEventManager.trigger('orderMgr.setItems.submit');

    return await this.withLoading('orderMgr.setItems.loading', async () => {
      try {
        const response =
          await this.rcmApiLibService.put<CartOrderUpdateHttpResponse>({
            url: this.getApiPath('orderItemApi'),
            urlParams: { orderId: 'current' },
            params: this.getParams(),
            data: items,
          });
        this.setBagDataFromResponse(response);
        this.rcmEventManager.trigger('orderMgr.setItems.success', {
          response,
          requestItems: items,
          previousItems,
        });
        return response;
      } catch (response) {
        this.handleHttpError(response);
        this.rcmEventManager.trigger('orderMgr.setItems.error', {
          response,
          requestItems: items,
          previousItems,
        });
        throw response;
      }
    });
  }

  public async addItems(
    items: CartOrderItem[],
    options: CartUpdateOptions
  ): Promise<void> {
    await Promise.all(items.map((item) => this.addItem(item, options)));
  }

  public redirectToCart() {
    this.$window.location = '/cart#/items' as any;
  }

  /**
   * Update discount level
   */
  public async setDiscountPercent(discountPercent: number) {
    this.onBeforeHttp();

    this.rcmEventManager.trigger('orderMgr.setDiscountPercent.submit');

    await this.withLoading('orderMgr.setDiscountPercent.loading', async () => {
      try {
        const response =
          await this.rcmApiLibService.put<CartOrderUpdateHttpResponse>({
            url: this.getApiPath('orderApi'),
            urlParams: { orderId: 'current' },
            params: this.getParams(),
            data: { discountPercent },
          });
        this.setBagDataFromResponse(response);
        this.rcmEventManager.trigger(
          'orderMgr.setDiscountPercent.success',
          response
        );
      } catch (response) {
        this.handleHttpError(response);
        this.rcmEventManager.trigger(
          'orderMgr.setDiscountPercent.error',
          response
        );
        throw response;
      }
    });
  }

  public async preProcessCurrentOrder() {
    this.onBeforeHttp();

    this.rcmEventManager.trigger('orderMgr.preProcessCurrentOrder.submit');

    await this.withLoading(
      'orderMgr.preProcessCurrentOrder.loading',
      async () => {
        try {
          const response =
            await this.rcmApiLibService.post<CartOrderUpdateHttpResponse>({
              url: this.getApiPath('preProcessCurrentOrderApi'),
              urlParams: { orderId: 'current' },
              data: null,
            });
          this.setBagDataFromResponse(response, false, true);
          this.rcmEventManager.trigger(
            'orderMgr.preProcessCurrentOrder.success',
            response
          );
        } catch (response) {
          this.handleHttpError(response as CartOrderUpdateHttpResponse);
          this.rcmEventManager.trigger(
            'orderMgr.preProcessCurrentOrder.error',
            response
          );
          throw response;
        }
      }
    );
  }

  private async _processCurrentOrder(
    payment: CartOrderPayment,
    execRequirements?: boolean
  ) {
    this.onBeforeHttp();

    if (this.executeCurrentPageRequirements()) {
      //console.error('Page requirements not met');
      this.rcmEventManager.trigger(
        'orderMgr.processCurrentOrder.pageRequirementsNotMet',
        'Page requirements not met'
      );
      return;
    }

    // Server should tell us about missing payment type
    // if (!payment.type) {
    //     console.error('Payment type is required of order processing');
    // }

    this.rcmEventManager.trigger('orderMgr.processCurrentOrder.submit');
    localStorage.removeItem('paymentError');

    await this.withLoading('orderMgr.processCurrentOrder.loading', async () => {
      try {
        const response =
          await this.rcmApiLibService.post<CartOrderUpdateHttpResponse>({
            url: this.getApiPath('processCurrentOrderApi'),
            urlParams: { orderId: 'current' },
            data: payment,
          });
        const mustRedirectToThirdPartyForPaymentMessage =
          response.messages?.find(
            (message) =>
              message.code ===
              'MUST_REDIRECT_TO_THIRD_PARTY_TO_COMPLETE_PAYMENT'
          );
        if (mustRedirectToThirdPartyForPaymentMessage) {
          window.location.href =
            mustRedirectToThirdPartyForPaymentMessage.params.redirectUrl;
          return;
        }

        this.setBagDataFromResponse(response, execRequirements);
        this.rcmEventManager.trigger(
          'orderMgr.processCurrentOrder.success',
          response
        );
        if (!!localStorage.getItem('isUsingGuestCheckout')) {
          localStorage.removeItem('isUsingGuestCheckout');
        }
        if (!!localStorage.getItem('userSetShipVia')) {
          localStorage.removeItem('userSetShipVia');
        }
        window.location.href =
          '/checkout/receipt?orderNumber=' + response.data.orderNumber;
      } catch (response) {
        localStorage.setItem('paymentError', JSON.stringify(response.messages));
        this.rcmEventManager.trigger(
          'orderMgr.processCurrentOrder.error',
          response
        );

        return response;
      }
    });
  }

  /**
   * Send payment for processing
   */
  public async processCurrentOrder(
    payment: CartOrderPayment,
    execRequirements?: boolean
  ): Promise<void> {
    if (typeof execRequirements === 'undefined') {
      execRequirements = false;
    }
    try {
      await this.preProcessCurrentOrder();
    } catch (response) {
      this.rcmEventManager.trigger(
        'orderMgr.processCurrentOrder.error',
        response
      );
      throw response;
    }
    await this._processCurrentOrder(payment, execRequirements);
  }

  /**
   * @todo 2021-04-23 - bharms@relivinc.com - axosoft/25604:
   *       Do we want this to trigger analytics events on the server?
   */
  public async patchOrder(
    order: CartOrder
  ): Promise<CartOrderUpdateHttpResponse> {
    this.onBeforeHttp();

    if (!order) {
      console.error('Order not defined');
      return;
    }

    this.rcmEventManager.trigger('orderMgr.patchOrder.submit');

    await this.withLoading('orderMgr.patchOrder.loading', async () => {
      try {
        const response =
          await this.rcmApiLibService.put<CartOrderUpdateHttpResponse>({
            url: this.getApiPath('orderApi'),
            urlParams: { orderId: 'current' },
            params: this.getParams(),
            data: order,
          });
        this.setBagDataFromResponse(response, null);
        this.rcmEventManager.trigger('orderMgr.patchOrder.success', response);
        return response;
      } catch (response) {
        localStorage.setItem('angularError', JSON.stringify(response.messages));
        this.handleHttpError(response);
        this.rcmEventManager.trigger('orderMgr.patchOrder.error', response);
        throw response;
      }
    });
  }

  public async refreshOrder(
    loadingListener?: LoadingCallback
  ): Promise<CartOrderUpdateHttpResponse> {
    this.onBeforeHttp();

    loadingListener = this.buildLoadingListener(loadingListener);

    this.rcmEventManager.trigger('orderMgr.refreshOrder.submit');
    loadingListener('orderMgr.refreshOrder.loading', 0);
    this.rcmEventManager.trigger('orderMgr.loading', true);

    try {
      const response =
        await this.rcmApiLibService.get<CartOrderUpdateHttpResponse>({
          url: this.getApiPath('orderApi'),
          urlParams: { orderId: 'current' },
          params: this.getParams(),
          data: this.order,
        });
      this.setBagDataFromResponse(response, null);
      this.rcmEventManager.trigger('orderMgr.refreshOrder.success', response);
      return response;
    } catch (response) {
      this.setBagDataFromResponse(response, null);
      this.rcmEventManager.trigger('orderMgr.refreshOrder.success', response);
      throw response;
    } finally {
      loadingListener('orderMgr.refreshOrder.loading', 1);
      this.rcmEventManager.trigger('orderMgr.loading', false);
    }
  }

  public async previewTotalsStart(
    shippingData: CartOrderShipping['data']
  ): Promise<void> {
    this.onBeforeHttp();

    this.rcmEventManager.trigger('orderMgr.previewTotals.start');

    this.order.shipping = {
      type: 'ShippingVistaDefault',
      data: {
        shipVia: shippingData.shipVia,
        shipAddress: { type: 'BasicShipAddress', ...shippingData.shipAddress },
      },
    };

    /**
     * @todo 2021-04-23 - bharms@relivinc.com - axosoft/25604:
     *       Do we want this to be handled on the server?
     */
    gtag('event', 'preview_totals', {});

    await this.withLoading('orderMgr.previewTotals.loading', async () => {
      try {
        const response = await this.rcmApiLibService.post<
          CartOrderUpdateHttpResponse | CartOrderPreviewTotalsHttpResponse
        >({
          url: '/api/cart/order/current/totals-preview',
          data: this.order,
        });
        const newOrder = { ...this.order };
        /**
         * There is an oddity with this HTTP API where for a 200s it just returns the totals
         * but for 400s it returns the whole order. This "if" statement deals with these two cases.
         */
        if (response.messages && response.messages.length !== 0) {
          this.setBagDataFromResponse(response);
        } else {
          newOrder.orderTotals = response.data as CartOrderTotals;
          this.setBagDataFromResponse({ data: newOrder });
        }
        this.rcmEventManager.trigger(
          'orderMgr.previewTotals.success',
          response
        );
      } catch (response) {
        this.handleHttpError(response);
        this.rcmEventManager.trigger('orderMgr.previewTotals.error', response);
        throw response;
      }
    });
  }

  public cartPage() {
    this.conditionalNavigation.defaultPage();
  }

  public async nextPage(refresh?: boolean) {
    this.rcmEventManager.trigger('orderMgr.nextPage', {
      currentPage: this.conditionalNavigation.getCurrentPage(),
      order: this.order,
    });

    if (refresh || !this.hasOrder()) {
      const response = await this.refreshOrder();
      this.conditionalNavigation.nextPage(
        this.getOrderConditions(response.data)
      );
      return;
    }

    this.conditionalNavigation.nextPage(this.getOrderConditions(this.order));
  }

  public executeCurrentPageRequirements(): boolean {
    const conditions = this.getOrderConditions(this.order);
    return this.conditionalNavigation.executeCurrentPageRequirements(
      conditions
    );
  }

  public executePageRequirements(page): boolean {
    const conditions = this.getOrderConditions(this.order);
    return this.conditionalNavigation.executePageRequirements(page, conditions);
  }

  public getOrderConditions(order: CartOrder): CartOrderCondition[] {
    if (!order) {
      return [];
    }
    if (!order.conditions) {
      return [];
    }
    return order.conditions;
  }

  /**
   * @todo 2021-04-23 - bharms@relivinc.com - axosoft/25604:
   *       This should be performed on the server.
   *       Queuing functionality should no longer be needed.
   *       Additional analytics events may no longer be needed?
   */
  private async sendCartUpdateAnalyticsEvents(
    item: CartOrderItem,
    { cart_update_method }: CartUpdateOptions
  ): Promise<void> {
    const previousQty = this.getSkuQty(item.skuNumber);
    const diff = item.qty - previousQty;
    const sign = diff > 0 ? 1 : diff < 0 ? -1 : 0;
    const event_name = (['remove_from_cart', , 'add_to_cart'] as const)[
      sign + 1
    ];
    const quantity = diff * sign;
    const event_params = {
      cart_update_method,
      items: [{ id: item.skuNumber, quantity }],
    };

    return await sendAnalyticsEvents({ event_name, event_params });
  }

  async withLoading<T>(eventName: string, fn: () => Promise<T>) {
    this.rcmLoading.setLoading(eventName, 0);
    this.rcmEventManager.trigger('orderMgr.loading', true);

    try {
      return await fn();
    } catch (e) {
      throw e;
    } finally {
      this.rcmLoading.setLoading(eventName, 1);
      this.rcmEventManager.trigger('orderMgr.loading', false);
    }
  }
}

angular.module('checkout').factory('orderMgr', [
  '$window',
  'rcmEventManager',
  'rcmApiLibService',
  'rcmApiLibMessageService',
  'rcmLoading',
  'conditionalNavigation',
  (
    $window: Window,
    rcmEventManager: IRcmApiLibEventManager,
    rcmApiLibService: IRcmApiLibService,
    rcmApiLibMessageService: IRcmApiLibMessageService,
    rcmLoading: IRcmLoading,
    conditionalNavigation: CartConditionalNavigation
  ) => {
    //Also make this service available to code outside angular via global window scope
    window.checkoutOrderManager = new CheckoutOrderManager(
      $window,
      rcmEventManager,
      rcmApiLibService,
      rcmApiLibMessageService,
      rcmLoading,
      conditionalNavigation
    );
    return window.checkoutOrderManager;
  },
]);
